import { FleetType, TrailerType } from "store/lobby/lobby-model";

type SaleChannelValue = number | { id?: number | null } | null | undefined;
type SaleChannelOption = { id?: number | null };

export type FleetFormValues = {
  saleChannelDescription?: number | null;
  trailerType?: number | null;
};

export type BackendFleetValues = {
  saleChannelId?: number | null;
  trailerType?: number | null;
};

interface IResolveFleetFormValues {
  currentValues: FleetFormValues;
  backendValues?: BackendFleetValues | null;
  hasSaleChannel: boolean;
  salesChannelList?: SaleChannelOption[] | null;
}

type RegisterWithSaleChannel = {
  saleChannel?: SaleChannelValue;
  saleChannelDescription?: unknown;
  trailerType?: number | null;
};

export const getSaleChannelId = (saleChannel?: SaleChannelValue) => {
  if (saleChannel == null) return null;

  return typeof saleChannel === "number" ? saleChannel : saleChannel.id ?? null;
};

export const isSaleChannelListReady = (salesChannelList?: SaleChannelOption[] | null) =>
  Array.isArray(salesChannelList) && (salesChannelList.length === 0 || salesChannelList.some(({ id }) => id != null));

export const normalizeTrailerTypeForSaleChannel = (trailerType?: number | null, saleChannelId?: number | null, enforceCompatibility = false) =>
  enforceCompatibility && trailerType === TrailerType.OTHER && saleChannelId !== FleetType.Others ? null : trailerType;

export const resolveFleetFormValues = ({ currentValues, backendValues, hasSaleChannel, salesChannelList }: IResolveFleetFormValues) => {
  const hasBackendSaleChannel = backendValues?.saleChannelId != null;
  const hasBackendTrailerType = backendValues?.trailerType != null;
  const hasBackendValues = hasBackendSaleChannel || hasBackendTrailerType;

  if (hasBackendSaleChannel && hasSaleChannel && !isSaleChannelListReady(salesChannelList)) {
    return { values: currentValues, hasBackendValues, pending: true };
  }

  const saleChannelDescription = hasBackendSaleChannel
    ? hasSaleChannel && salesChannelList?.some(({ id }) => id === backendValues.saleChannelId)
      ? backendValues.saleChannelId
      : null
    : currentValues.saleChannelDescription;
  const trailerType = normalizeTrailerTypeForSaleChannel(hasBackendTrailerType ? backendValues.trailerType : currentValues.trailerType, saleChannelDescription, hasBackendValues);

  return {
    values: { saleChannelDescription, trailerType },
    hasBackendValues,
    pending: false,
  };
};

export const getSaleChannelFormValue = (register?: RegisterWithSaleChannel | null, hasSaleChannel = true, salesChannelList?: SaleChannelOption[] | null) => {
  if (!hasSaleChannel) return null;

  const saleChannelId = getSaleChannelId(register?.saleChannel);
  if (saleChannelId == null) return null;

  return salesChannelList?.some(({ id }) => id === saleChannelId) ? saleChannelId : null;
};

export const mountRegisterFormValues = <T extends RegisterWithSaleChannel | null | undefined>(
  register: T,
  hasSaleChannel = true,
  salesChannelList?: SaleChannelOption[] | null,
  enforceTrailerTypeCompatibility = false
) => {
  if (!register) return register;

  const saleChannelDescription = getSaleChannelFormValue(register, hasSaleChannel, salesChannelList);

  const formValues = {
    ...register,
    saleChannelDescription,
  };

  if (enforceTrailerTypeCompatibility && register.trailerType === TrailerType.OTHER) {
    formValues.trailerType = normalizeTrailerTypeForSaleChannel(register.trailerType, saleChannelDescription, true);
  }

  return formValues;
};
